frappe.provide("erpnext.accounts"); // @dokos

/**
 * Payment collection wiring for Sales Invoice, Sales Order and Subscription. // @dokos
 *
 * Adds a single functional "Request Payment" entry point and renders the payment
 * lifecycle in the dedicated "Payment Collection" tab as a status card, a progress
 * stepper and a collapsible "Details" timeline — so users can see where a payment
 * stands, spot a failure and fix it without opening the technical Payment Request or
 * the System-Manager-only Payment Session Log.
 *
 * The timeline is fed by the gateway-agnostic Payment Session Log data returned by
 * `get_payment_request_context`, so it renders identically for every v2 gateway.
 * These handlers are *additive*: they stack on top of the doctype's own form scripts.
 */

const PR_CONTEXT_METHOD =
	"erpnext.accounts.doctype.payment_request.payment_request.get_payment_request_context";
// @dokos: `resend_payment_email` is a permission-checked *document* method (upstream #57201
// removed the module-level whitelisted wrapper this used to call), so it has to be reached
// through `run_doc_method` — this card renders from a plain object, not a loaded form.
const RESEND_EMAIL_METHOD = "resend_payment_email";

// Payment Request status → Espresso badge theme (fallback when a request has no gateway session).
const REQUEST_BADGE_THEME = {
	Draft: "gray",
	Requested: "amber",
	Initiated: "amber",
	Pending: "blue",
	"Partially Paid": "blue",
	Paid: "green",
	Completed: "green",
	Failed: "red",
	Cancelled: "gray",
};

// Payment Session Log status → Espresso badge theme. Escalated sessions are handled separately.
const SESSION_BADGE_THEME = {
	Created: "gray",
	Initiated: "amber",
	Started: "amber",
	Queued: "blue",
	Processing: "blue",
	Paid: "green",
	Failed: "red",
	Error: "amber",
	Abandoned: "gray",
	Refunded: "violet",
	"Partially Refunded": "violet",
};

const OPEN_REQUEST_STATES = ["Draft", "Requested", "Initiated", "Pending", "Partially Paid"];

erpnext.accounts.setup_payment_collection = function (frm) {
	if (frm.is_new() || frm.doc.is_return) return;

	// Sales Invoice / Sales Order only make sense once submitted; Subscription is
	// not submittable, so a saved document is enough.
	if (["Sales Invoice", "Sales Order"].includes(frm.doctype) && frm.doc.docstatus !== 1) {
		return;
	}

	frappe.call({
		method: PR_CONTEXT_METHOD,
		args: { reference_doctype: frm.doctype, reference_name: frm.doc.name },
		callback: ({ message }) => {
			if (message && message.eligible) {
				erpnext.accounts._add_request_button(frm, message);
			}
			erpnext.accounts._render_payment_section(frm, message);
		},
	});
};

erpnext.accounts._open_panel = function (frm, context) {
	new erpnext.accounts.PaymentRequestPanel({ frm, context }).show();
};

erpnext.accounts._add_request_button = function (frm, context) {
	if (!context.can_create || flt(context.amount_due) <= 0) return;
	if (!frappe.boot.user.in_create.includes("Payment Request")) return;

	frm.add_custom_button(
		__("Request Payment"),
		() => erpnext.accounts._open_panel(frm, context),
		__("Create")
	);
};

// ---------------------------------------------------------------------------
// Section renderer
// ---------------------------------------------------------------------------

erpnext.accounts._render_payment_section = function (frm, context) {
	const field = frm.get_field("dokos_payment_request_status");
	if (!field || !field.$wrapper) return;

	const $wrap = field.$wrapper.empty().addClass("pc-section");
	const requests = (context && context.payment_requests) || [];
	const sessions = (context && context.payment_sessions) || [];

	const timelineField = frm.get_field("dokos_payment_timeline");
	const $timeline =
		timelineField && timelineField.$wrapper && timelineField.$wrapper.empty().addClass("pc-timeline");

	if (!requests.length && !sessions.length) {
		erpnext.accounts._render_empty(frm, context, $wrap);
		frm.toggle_display("dokos_payment_details_section", false);
		return;
	}

	const request = requests[0];
	const session = sessions[0];
	const currency =
		(session && session.currency) || (request && request.currency) || (context && context.currency);

	$wrap.append(erpnext.accounts._render_summary(frm, context, request, session, currency));
	$wrap.append(erpnext.accounts._render_stepper(request, session));

	// The timeline lives in a native collapsible "Details" form section, not a custom widget.
	frm.toggle_display("dokos_payment_details_section", true);
	if ($timeline) erpnext.accounts._render_timeline(request, sessions, currency, $timeline);
};

erpnext.accounts._render_empty = function (frm, context, $wrap) {
	const eligible = context && context.eligible && flt(context.amount_due) > 0;
	$wrap.append($('<div class="pc-empty">').text(__("No payment has been requested yet.")));
	if (eligible && context.can_create) {
		$wrap.append(
			frappe.ui.button({
				label: __("Request Payment"),
				variant: "solid",
				size: "sm",
				icon: "plus",
				onclick: () => erpnext.accounts._open_panel(frm, context),
			})
		);
	}
};

// ---------------------------------------------------------------------------
// Summary card
// ---------------------------------------------------------------------------

erpnext.accounts._render_summary = function (frm, context, request, session, currency) {
	const stage = erpnext.accounts._flow_stage(request, session);
	const gateway = session && session.payment_gateway;
	const amount = format_currency(
		flt((session && session.amount) || (request && request.grand_total) || context.amount_due),
		currency
	);

	const $card = $(`<div class="pc-summary pc-tone-${stage.tone}">`);
	const $main = $('<div class="pc-summary-main">').appendTo($card);
	$('<div class="pc-amount">').text(amount).appendTo($main);

	const $headline = $('<div class="pc-headline">').appendTo($main);
	$headline.append(
		frappe.ui.badge({
			label: erpnext.accounts._status_label(request, session),
			theme: erpnext.accounts._status_theme(request, session),
		})
	);
	$('<span class="pc-headline-text">')
		.text(erpnext.accounts._headline_text(stage, gateway))
		.appendTo($headline);

	const sub = erpnext.accounts._sub_text(stage, request, session);
	if (sub) $('<div class="pc-sub">').text(sub).appendTo($main);

	const $actions = $('<div class="pc-actions">').appendTo($card);
	erpnext.accounts
		._summary_actions(frm, context, request, session, stage)
		.forEach(($btn) => $actions.append($btn));

	const alert = erpnext.accounts._summary_alert(stage, session);
	if (alert) $card.append(frappe.ui.alert(alert));

	return $card;
};

erpnext.accounts._summary_alert = function (stage, session) {
	if (!session || !session.error_message) return null;
	if (stage.key === "failed") {
		return { title: __("Why it failed"), description: session.error_message, theme: "red" };
	}
	if (stage.key === "stuck") {
		return { title: __("Gateway response"), description: session.error_message, theme: "yellow" };
	}
	return null;
};

erpnext.accounts._summary_actions = function (frm, context, request, session, stage) {
	const buttons = [];
	const btn = (opts) => frappe.ui.button(Object.assign({ size: "sm" }, opts));
	const isOpen = request && OPEN_REQUEST_STATES.includes(request.status);

	if (stage.key === "paid" || stage.key === "refunded") {
		if (session && session.payment_entry) {
			buttons.push(
				btn({
					label: __("View payment entry"),
					variant: "solid",
					icon: "receipt",
					onclick: () => frappe.set_route("Form", "Payment Entry", session.payment_entry),
				})
			);
		}
	} else if (stage.key === "stuck") {
		if (session && session.reconciliation_issue) {
			buttons.push(
				btn({
					label: __("Open reconciliation issue"),
					variant: "solid",
					icon: "alert-triangle",
					onclick: () =>
						frappe.set_route(
							"Form",
							"Payment Reconciliation Issue",
							session.reconciliation_issue
						),
				})
			);
		}
	} else if (stage.key === "failed") {
		if (context.can_create && flt(context.amount_due) > 0) {
			buttons.push(
				btn({
					label: __("Send a new payment link"),
					variant: "solid",
					icon: "plus",
					onclick: () => erpnext.accounts._open_panel(frm, context),
				})
			);
		}
	} else {
		// Awaiting payment / processing.
		if (request && request.payment_url) {
			buttons.push(
				btn({
					label: __("Copy payment link"),
					variant: "outline",
					icon: "copy",
					onclick: () => {
						frappe.utils.copy_to_clipboard(request.payment_url);
					},
				})
			);
		}
		if (isOpen && request && request.email_to && !request.mute_email) {
			buttons.push(
				btn({
					label: __("Resend email"),
					variant: "outline",
					icon: "mail",
					onclick: () =>
						frappe
							.xcall("run_doc_method", {
								dt: "Payment Request",
								dn: request.name,
								method: RESEND_EMAIL_METHOD,
							})
							.then(() => frappe.show_alert({ message: __("Email sent"), indicator: "green" })),
				})
			);
		}
	}

	// A session that is still running reflects whatever the scheduled reconciler last wrote;
	// a plain refresh re-reads that state without touching the gateway.
	if (session && !session.terminal) {
		buttons.push(
			btn({
				label: __("Refresh"),
				variant: "ghost",
				icon: "refresh-cw",
				onclick: () => erpnext.accounts.setup_payment_collection(frm),
			})
		);
	}

	const overflow = erpnext.accounts._overflow_actions(frm, request, isOpen);
	if (overflow.length) {
		buttons.push(
			frappe.ui.dropdown({
				button: { label: __("More"), variant: "ghost", size: "sm", icon: "more-horizontal" },
				options: overflow,
			})
		);
	}

	return buttons;
};

erpnext.accounts._overflow_actions = function (frm, request, isOpen) {
	const options = [];
	if (request) {
		options.push({
			label: __("View payment request"),
			icon: "external-link",
			onclick: () => frappe.set_route("Form", "Payment Request", request.name),
		});
	}
	if (request && isOpen && request.docstatus === 1) {
		options.push({
			label: __("Cancel request"),
			icon: "x",
			theme: "red",
			onclick: () =>
				frappe.confirm(__("Cancel this payment request?"), () =>
					frappe
						.xcall("frappe.client.cancel", { doctype: "Payment Request", name: request.name })
						.then(() => {
							frappe.show_alert({ message: __("Request cancelled"), indicator: "orange" });
							erpnext.accounts.setup_payment_collection(frm);
						})
				),
		});
	}
	return options;
};

// ---------------------------------------------------------------------------
// Progress stepper
// ---------------------------------------------------------------------------

erpnext.accounts._render_stepper = function (request, session) {
	const stage = erpnext.accounts._flow_stage(request, session);
	const labels = [__("Requested"), __("Awaiting payment"), __("Processing"), __("Paid")];

	const $stepper = $('<div class="pc-stepper">');
	labels.forEach((label, index) => {
		const state = erpnext.accounts._step_state(index, stage);
		const $step = $(`<div class="pc-step pc-step-${state}">`).appendTo($stepper);
		$('<span class="pc-step-bar">').appendTo($step);
		$('<span class="pc-step-node">').html(erpnext.accounts._step_glyph(state)).appendTo($step);
		$('<span class="pc-step-label">').text(label).appendTo($step);
	});
	return $stepper;
};

erpnext.accounts._step_state = function (index, stage) {
	if (index < stage.reached) return "done";
	if (index > stage.reached) return "pending";
	// The reached step carries the outcome.
	if (stage.key === "paid" || stage.key === "refunded") return "paid";
	if (stage.key === "failed") return "failed";
	if (stage.key === "stuck") return "stuck";
	return "active";
};

erpnext.accounts._step_glyph = function (state) {
	if (state === "done" || state === "paid") return "&check;";
	if (state === "failed") return "&times;";
	if (state === "stuck") return "!";
	return "";
};

// ---------------------------------------------------------------------------
// Details timeline (collapsed)
// ---------------------------------------------------------------------------

erpnext.accounts._render_timeline = function (request, sessions, currency, $wrap) {
	// One continuous timeline: the request and every gateway attempt are a single payment
	// journey, so their events share one line instead of sitting in separate boxes.
	const events = erpnext.accounts._timeline_events(request, sessions);
	const lastUpdate = events.length ? events[0].when : null;

	$('<div class="pc-timeline-meta">')
		.text(erpnext.accounts._details_meta(events.length, lastUpdate))
		.appendTo($wrap);

	const $events = $('<ol class="pc-events">').appendTo($wrap);
	events.forEach((event) => $events.append(erpnext.accounts._render_event(event)));
};

erpnext.accounts._details_meta = function (eventCount, lastUpdate) {
	const parts = [__("{0} events", [eventCount])];
	if (lastUpdate) {
		parts.push(__("last update {0}", [frappe.datetime.str_to_user(lastUpdate)]));
	}
	return parts.join(" · ");
};

// Flatten the request and every session into one newest-first list of events. Sessions already
// arrive newest attempt first; each doc id (Payment Session Log, Payment Entry, Payment Request) is
// attached to the event it belongs to, so nothing needs a separate per-attempt header.
erpnext.accounts._timeline_events = function (request, sessions) {
	const items = [];

	(sessions || []).forEach((session) => {
		const events = (session.events || [])
			.filter((event) => event.from_status !== event.to_status)
			.filter((event) => !(event.message || "").startsWith("Rejected transition"))
			.map((event) => ({
				when: event.timestamp,
				label: erpnext.accounts._event_label(event, session.payment_gateway),
				source: event.source,
				tone: erpnext.accounts._event_tone(event.to_status),
				link: erpnext.accounts._event_link(event, session),
			}))
			.reverse();
		items.push(...events);
	});

	if (request) {
		if (request.email_to && !request.mute_email) {
			items.push({
				when: request.creation,
				label: __("Payment link emailed to {0}", [request.email_to]),
				source: "system",
			});
		}
		items.push({
			when: request.creation,
			label: __("Payment requested"),
			source: "user",
			link: { doctype: "Payment Request", name: request.name },
		});
	}

	// Present strictly newest first. Timestamps are ISO-like, so a lexical compare is chronological;
	// Array.sort is stable, so events sharing a timestamp keep their insertion order.
	return items.sort((a, b) => (a.when < b.when ? 1 : a.when > b.when ? -1 : 0));
};

// The document a transition points at: the session log when it is created, the Payment Entry once paid.
erpnext.accounts._event_link = function (event, session) {
	if (event.to_status === "Initiated") {
		return { doctype: "Payment Session Log", name: session.name };
	}
	if (event.to_status === "Paid" && session.payment_entry) {
		return { doctype: "Payment Entry", name: session.payment_entry };
	}
	return null;
};

erpnext.accounts._render_event = function (event) {
	const $event = $(`<li class="pc-event pc-event-${event.tone || "default"}">`);
	$('<span class="pc-event-dot">').appendTo($event);

	const $content = $('<div class="pc-event-content">').appendTo($event);
	const $row = $('<div class="pc-event-row">').appendTo($content);
	$('<time class="pc-event-time">')
		.text(event.when ? frappe.datetime.str_to_user(event.when) : "")
		.appendTo($row);
	$('<span class="pc-event-label">').text(event.label).appendTo($row);
	if (event.source) {
		$('<span class="pc-event-source">').text(event.source).appendTo($row);
	}
	if (event.link && event.link.name) {
		$('<a class="pc-event-link">')
			.text(event.link.name)
			.attr(
				"href",
				`/app/${frappe.router.slug(event.link.doctype)}/${encodeURIComponent(event.link.name)}`
			)
			.on("click", (clickEvent) => {
				clickEvent.preventDefault();
				frappe.set_route("Form", event.link.doctype, event.link.name);
			})
			.appendTo($row);
	}
	return $event;
};

// ---------------------------------------------------------------------------
// State mapping — gateway-agnostic, driven by the Payment Session Log status
// ---------------------------------------------------------------------------

/**
 * Reduce the raw status into a stage the tab can render: how far the flow got
 * (`reached`, an index into the 4-step stepper) and its `key`/`tone` outcome.
 */
erpnext.accounts._flow_stage = function (request, session) {
	if (session) {
		switch (session.status) {
			case "Paid":
				return { reached: 3, key: "paid", tone: "green" };
			case "Refunded":
			case "Partially Refunded":
				return { reached: 3, key: "refunded", tone: "neutral" };
			case "Failed":
				return { reached: 2, key: "failed", tone: "red" };
			case "Error":
				return session.escalated
					? { reached: 2, key: "stuck", tone: "amber" }
					: { reached: 2, key: "failed", tone: "red" };
			case "Abandoned":
				return { reached: 1, key: "failed", tone: "red" };
			case "Queued":
			case "Processing":
				return { reached: 2, key: "processing", tone: "neutral" };
			default:
				return { reached: 1, key: "awaiting", tone: "neutral" };
		}
	}

	const status = request && request.status;
	if (["Paid", "Completed"].includes(status)) return { reached: 3, key: "paid", tone: "green" };
	if (status === "Failed") return { reached: 2, key: "failed", tone: "red" };
	if (status === "Partially Paid") return { reached: 2, key: "processing", tone: "neutral" };
	return { reached: 1, key: "awaiting", tone: "neutral" };
};

erpnext.accounts._headline_text = function (stage, gateway) {
	const gw = gateway || __("the gateway");
	switch (stage.key) {
		case "paid":
			return gateway ? __("Paid via {0}", [gateway]) : __("Payment received");
		case "refunded":
			return __("Payment refunded");
		case "stuck":
			return __("Needs attention — payment not confirmed");
		case "failed":
			return __("Payment failed");
		case "processing":
			return __("{0} is processing the payment", [gw]);
		default:
			return __("Waiting for the customer to pay");
	}
};

erpnext.accounts._sub_text = function (stage, request, session) {
	if (stage.key === "stuck") {
		return __(
			"The gateway never confirmed this payment and automatic checks have stopped. Check the gateway before requesting payment again — the amount may already have been collected."
		);
	}
	if (stage.key === "processing" && session && session.next_poll_at) {
		// Plain text only: the sub-line is rendered with .text(), so comment_when()'s HTML span
		// would show as raw markup.
		return __("Confirmation usually arrives within a few minutes. Next automatic check at {0}.", [
			frappe.datetime.str_to_user(session.next_poll_at),
		]);
	}
	if (stage.key === "awaiting" && request && request.email_to) {
		return __("Payment link sent to {0}.", [request.email_to]);
	}
	return "";
};

erpnext.accounts._status_label = function (request, session) {
	if (session) return erpnext.accounts._session_status_label(session);
	return __((request && request.status) || "Draft");
};

erpnext.accounts._status_theme = function (request, session) {
	if (session) {
		return session.escalated ? "amber" : SESSION_BADGE_THEME[session.status] || "gray";
	}
	return REQUEST_BADGE_THEME[(request && request.status) || "Draft"] || "gray";
};

erpnext.accounts._session_status_label = function (session) {
	if (session.escalated && session.status === "Error") return __("Needs attention");
	const labels = {
		Created: __("Awaiting payment"),
		Initiated: __("Awaiting payment"),
		Started: __("Awaiting payment"),
		Queued: __("Processing"),
		Processing: __("Processing"),
		Paid: __("Paid"),
		Failed: __("Failed"),
		Error: __("Not confirmed"),
		Abandoned: __("Abandoned"),
		Refunded: __("Refunded"),
		"Partially Refunded": __("Partially refunded"),
	};
	return labels[session.status] || __(session.status);
};

erpnext.accounts._event_label = function (event, gateway) {
	const gw = gateway || __("the gateway");
	switch (event.to_status) {
		case "Initiated":
			return __("Payment session created at {0}", [gw]);
		case "Started":
			return __("Payment link ready");
		case "Queued":
			return __("Payment queued at {0}", [gw]);
		case "Processing":
			return __("{0} is processing the payment", [gw]);
		case "Paid":
			return __("Payment confirmed by {0}", [gw]);
		case "Failed":
			return __("Payment failed");
		case "Error":
			return __("Payment could not be confirmed");
		case "Abandoned":
			return __("Attempt closed");
		case "Refunded":
			return __("Payment refunded");
		case "Partially Refunded":
			return __("Payment partially refunded");
		default:
			return event.message || __(event.to_status);
	}
};

erpnext.accounts._event_tone = function (toStatus) {
	if (["Paid", "Refunded", "Partially Refunded"].includes(toStatus)) return "green";
	if (toStatus === "Failed") return "red";
	if (toStatus === "Error") return "amber";
	return "default";
};

["Sales Invoice", "Sales Order", "Subscription"].forEach((doctype) => {
	frappe.ui.form.on(doctype, {
		refresh(frm) {
			erpnext.accounts.setup_payment_collection(frm);
		},
	});
});
